/**
 * PoleList Component - List of poles in sidebar
 */
import React, { useEffect, useState, useMemo } from 'react';
import Fuse from 'fuse.js';
import { useProjectStore, usePoleStore } from '../../stores';
import { useAuthStore } from '../../stores/authStore';
import { useRezimPrace } from '../../store/roleViewStore';
import { CassetteEditorDialog } from '../Dialogs/CassetteEditorDialog';
import { InstallationChecklistDialog } from '../Dialogs/InstallationChecklistDialog';
import { InstallationTechDialog } from '../Dialogs/InstallationTechDialog';
import { Pole } from '../../types';
import './PoleList.css';

export const PoleList: React.FC = () => {
  const { currentProject } = useProjectStore();
  const { poles, selectedPole, loading, fetchPoles, setSelectedPole } = usePoleStore();
  const { isAdmin } = useAuthStore();
  const { jePripajanie } = useRezimPrace();
  const [showCassetteEditor, setShowCassetteEditor] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [editingPole, setEditingPole] = useState<Pole | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (currentProject) {
      fetchPoles(currentProject.id);
    }
  }, [currentProject, fetchPoles]);

  // Natural sort function for pole names (Stĺpik_1, Stĺpik_2, ..., Stĺpik_10, ...)
  const naturalSort = (a: Pole, b: Pole): number => {
    const nameA = a.name || '';
    const nameB = b.name || '';

    // Split into parts: text and numbers
    const partsA = nameA.match(/(\d+|\D+)/g) || [];
    const partsB = nameB.match(/(\d+|\D+)/g) || [];

    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const partA = partsA[i] || '';
      const partB = partsB[i] || '';

      // If both parts are numbers, compare numerically
      const numA = parseInt(partA, 10);
      const numB = parseInt(partB, 10);

      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) return numA - numB;
      } else {
        // Otherwise compare as strings
        if (partA !== partB) return partA.localeCompare(partB);
      }
    }

    return 0;
  };

  // Filter out virtual poles (customer cassettes) and sort naturally
  const nonVirtualPoles = useMemo(() => {
    return poles.filter(p => !p.is_virtual).sort(naturalSort);
  }, [poles]);

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(nonVirtualPoles, {
      keys: [
        { name: 'name', weight: 2 },           // Name is most important
        { name: 'pole_type', weight: 1 },
        { name: 'notes', weight: 0.5 },
        { name: 'latitude', weight: 0.3 },
        { name: 'longitude', weight: 0.3 },
      ],
      threshold: 0.4,  // 0 = exact match, 1 = match anything
      includeScore: true,
      minMatchCharLength: 1,
    });
  }, [nonVirtualPoles]);

  // Filter poles based on search query and sort naturally
  const filteredPoles = useMemo(() => {
    if (!searchQuery.trim()) {
      return nonVirtualPoles;
    }
    const results = fuse.search(searchQuery);
    return results.map(result => result.item).sort(naturalSort);
  }, [nonVirtualPoles, searchQuery, fuse]);

  if (!currentProject) {
    return (
      <div className="pole-list-empty">
        <p>Nie je vybraný projekt</p>
        <p className="hint">Najprv vyberte projekt</p>
      </div>
    );
  }

  const getPoleIcon = (poleType: string) => {
    switch (poleType) {
      case 'olt':
        return '🏢';
      case 'manhole':
        return '🕳️';
      case 'splitter_point':
        return '🔀';
      case 'building':
        return '🏠';
      default:
        return '📍';
    }
  };

  const getPoleTypeLabel = (poleType: string) => {
    switch (poleType) {
      case 'olt':
        return 'OLT';
      case 'pole':
        return 'Stĺpik';
      case 'splitter_point':
        return 'Splitter';
      case 'building':
        return 'Budova';
      default:
        return 'Stĺpik';
    }
  };

  const handleOpenCassetteEditor = (pole: Pole, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingPole(pole);
    setShowCassetteEditor(true);
  };

  const handleCloseCassetteEditor = () => {
    setShowCassetteEditor(false);
    setEditingPole(null);
    if (currentProject) {
      fetchPoles(currentProject.id);
    }
  };

  return (
    <div className="pole-list">
      <div className="pole-list-header">
        <h3>Stĺpiky</h3>
        <span className="pole-count">{filteredPoles.length}/{nonVirtualPoles.length}</span>
      </div>

      {/* Search Input */}
      <div className="pole-search">
        <input
          type="text"
          placeholder="🔍 Hľadať stĺpik..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pole-search-input"
        />
        {searchQuery && (
          <button
            className="pole-search-clear"
            onClick={() => setSearchQuery('')}
            title="Vymazať"
          >
            ✕
          </button>
        )}
      </div>

      {loading && nonVirtualPoles.length === 0 ? (
        <div className="loading">Načítavajú sa stĺpiky...</div>
      ) : (
        <div className="pole-items">
          {nonVirtualPoles.length === 0 ? (
            <div className="empty-state">
              <p>Zatiaľ žiadne stĺpiky</p>
              <p className="empty-state-hint">
                Pridajte stĺpiky z mapy
              </p>
            </div>
          ) : filteredPoles.length === 0 ? (
            <div className="empty-state">
              <p>Žiadne výsledky</p>
              <p className="empty-state-hint">
                Skúste iný vyhľadávací výraz
              </p>
            </div>
          ) : (
            filteredPoles.map((pole) => (
              <div
                key={pole.id}
                className={`pole-item ${
                  selectedPole?.id === pole.id ? 'active' : ''
                }`}
                onClick={() => setSelectedPole(pole)}
              >
                <div className="pole-item-icon">{getPoleIcon(pole.pole_type)}</div>
                <div className="pole-item-content">
                  <div className="pole-item-name">{pole.name}</div>
                  <div className="pole-item-type">
                    {getPoleTypeLabel(pole.pole_type)}
                    {pole.splitter_type && ` (${pole.splitter_type})`}
                  </div>
                  {pole.pole_type === 'olt' && pole.olt_port_count && pole.olt_port_count > 0 && (
                    <div className="pole-item-info">
                      🔌 {pole.olt_port_count} portov
                    </div>
                  )}
                  {pole.cassette && pole.cassette.has_cassette && (
                    <div className="pole-item-info">
                      📦 Kazeta
                      {pole.cassette.splices && pole.cassette.splices.length > 0 && (
                        <span> • {pole.cassette.splices.length} zvarov</span>
                      )}
                      {pole.cassette.splitters && pole.cassette.splitters.length > 0 && (
                        <span> • {pole.cassette.splitters.length} splittrov</span>
                      )}
                    </div>
                  )}
                  {pole.endpoint_type !== 'none' && (
                    <div className="pole-item-endpoint">
                      {pole.endpoint_type === 'building'
                        ? `${pole.customer_count} zákazníkov`
                        : 'Jeden zákazník'}
                    </div>
                  )}
                </div>
                {pole.cassette && pole.cassette.has_cassette && (
                  <>
                    <button
                      className="pole-item-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingPole(pole);
                        setShowChecklist(true);
                      }}
                      title="📋 Inštalačný checklist"
                      style={{ marginRight: '8px' }}
                    >
                      📋
                    </button>
                    <button
                      className="pole-item-action"
                      onClick={(e) => handleOpenCassetteEditor(pole, e)}
                      title="Upraviť kazetu"
                    >
                      📦
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {editingPole && (
        <CassetteEditorDialog
          isOpen={showCassetteEditor}
          onClose={handleCloseCassetteEditor}
          pole={editingPole}
        />
      )}

      {showChecklist && editingPole && (
        <>
          {jePripajanie ? (
            <InstallationTechDialog
              pole={editingPole}
              projectId={editingPole.project_id}
              onClose={() => {
                setShowChecklist(false);
                setEditingPole(null);
              }}
            />
          ) : (
            <InstallationChecklistDialog
              pole={editingPole}
              onClose={() => {
                setShowChecklist(false);
                setEditingPole(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

